import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { useTranslation } from 'react-i18next';
import type {
  ISchedulerData,
  ISchedulerDependency,
  ISchedulerTask,
  GroupId,
  SchedulerPanelTab,
} from '@interfaces/IScheduler';
import { groupColorForGroup } from '../../lib/taskColor';
import { taskDatesProblem, taskWindowLabel } from '../../lib/taskDates';
import { ancestors, type IAncestor } from '../../lib/taskAncestors';
import { Tabs } from '@components/ui/Tabs';
import { TaskPanelEditTab } from './TaskPanelEditTab';
import { TaskPanelOverviewTab } from './TaskPanelOverviewTab';
import { TaskLadderTrail } from './TaskLadderTrail';
import { TaskStepActions } from './TaskStep';
import { TaskStatusPill } from '../TaskStatusPill/TaskStatusPill';
import { CommitReview } from '../CommitReview/CommitReview';
import { CommitReviewMany } from '../CommitReview/CommitReviewMany';
import { leafDescendants } from '../../lib/commitReadiness';
import { TaskPanelCommentsTab } from './TaskPanelCommentsTab';
import { TaskPanelStockTab } from './TaskPanelStockTab';
import { StockDeliveryDialog } from './StockDeliveryDialog';
import { TaskPanelStockDetail } from './TaskPanelStockDetail';
import { useCompanionState } from '@components/ui/Drawer/useCompanionState';
import type { IOrderHandoff } from '../../../LogisticsPage/orderHandoff';
import { useSchedulerPanelTab, useSchedulerActions, useSchedulerCommitTaskId, useSchedulerStale, useSchedulerTask } from '../../hooks/useSchedulerStore';
import { useTaskStockClaimsPersist } from '../../hooks/useTaskStockClaimsPersist';
import { useTaskPlanAutoSave } from '../../hooks/useTaskPlanAutoSave';
import { sameClaims } from '../../stock/sameClaims';
import { useSchedulerPortalContainer } from '../../context/SchedulerPortalContainer';
import { SchedulerCapabilitiesContext, useResolveSchedulerCapabilities } from '../../context/SchedulerCapabilities';
import { TaskPanelStockReadOnly } from './TaskPanelStockReadOnly';
import styles from './TaskPanel.module.css';
import { rebaseDraft } from '../../lib/rebaseDraft';
import { sameJson } from '../../lib/sameJson';
import { useSelector } from 'react-redux';
import { useSiteToday } from '@d-orca/shared/client/hooks';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { newTaskTemplate } from '../../lib/newTaskTemplate';

// ---------------------------------------------------------------------------
// TaskPanel
// ---------------------------------------------------------------------------

interface ITaskPanelProps {
  open: boolean;
  mode: 'create' | 'edit';
  data: ISchedulerData;
  /** Loaded schedule number — threaded to the Overview's immediate writes. */
  scheduleNumber?: string;
  taskId?: string;
  prefillGroupId?: GroupId;
  prefillTask?: Partial<ISchedulerTask>;
  openGroupSelect?: boolean;
  /**
   * Whether structural edits (the Edit tab — dates, dependencies, group — the
   * Stock tab, and Delete) are offered. The Scheduler passes true (its toolbar
   * owns Save). The Activities drawer passes false: it has no Save-to-Genba
   * flow, so a structural edit made there would be silently lost — only the
   * immediate-write surfaces (status, progress, locations, comments) are
   * exposed. Stock claims are an EXECUTION write now (ruling 9: committed
   * through their own per-task PUT, see `commitStockClaims`), but the tab
   * stays behind this gate for now — its drafted edits still ride the Edit
   * tab's Save/Cancel footer, which the read-only host does not render.
   * Defaults to true.
   */
  allowStructureEdit?: boolean;
  /** Extra content rendered at the top of the Overview tab (e.g. the Activities
   *  Risk Assessment). Kept as a slot so the Scheduler stays decoupled from it. */
  overviewSlot?: ReactNode;
  onClose: () => void;
  onSave: (task: ISchedulerTask) => void;
  /** Stock tab: deep-link to a delivery on the Logistics page. Hosts without
   *  a router (or without the Stock tab) leave it out. */
  onOpenDelivery?: (deliveryRef: string) => void;
  onDelete?: (taskId: string) => void;
  onAddDependency?: (dependency: ISchedulerDependency) => void;
  onUpdateDependency?: (dependency: ISchedulerDependency) => void;
  onDeleteDependency?: (dependencyId: string) => void;
}

/** How many summaries the header's breadcrumb shows before folding the rest. */
const MAX_TRAIL = 2;

/** A crumb of the trail: a summary you can go to, or the ellipsis standing in
 *  for the ones folded away (no task behind it, so nowhere to go). */
type ITrailStep = IAncestor | { id: null; name: '…' };

/** The ancestors the header shows: the deepest few, with an ellipsis standing
 *  in for the ones folded away, so the breadcrumb stays one short line. */
function visibleTrail(chain: readonly IAncestor[]): ITrailStep[] {
  if (chain.length <= MAX_TRAIL) return [...chain];
  return [{ id: null, name: '…' }, ...chain.slice(-MAX_TRAIL)];
}

export function TaskPanel({ open, mode, data, scheduleNumber, taskId, prefillGroupId, prefillTask, openGroupSelect = false, allowStructureEdit = true, overviewSlot, onClose, onSave, onOpenDelivery, onDelete, onAddDependency, onUpdateDependency, onDeleteDependency }: ITaskPanelProps) {
  const { t } = useTranslation();
  const portalContainer = useSchedulerPortalContainer();
  // Resolved once for the whole drawer — it is also hosted outside the Scheduler page.
  const capabilities = useResolveSchedulerCapabilities();
  const { canEdit, canExecute } = capabilities;
  const [draft, setDraft] = useState<ISchedulerTask | null>(null);
  // The delivery dialog raised from the Stock tab, mounted inside the drawer
  // so the drawer's own dismiss logic treats it as its own.
  const [deliveryHandoff, setDeliveryHandoff] = useState<IOrderHandoff | null>(null);
  // Lifted from the Stock tab via its `onValidity` callback (not a DOM
  // attribute) — the footer disables Save on it while that tab is active.
  const [stockSaveBlocked, setStockSaveBlocked] = useState(false);
  // Lifted the same way: while the Stock tab's element list is open it owns
  // the drawer's bottom bar (Cancel / Confirm selection), so the drawer's own
  // Save/Cancel footer is not rendered for as long as it is.
  const [stockPicking, setStockPicking] = useState(false);
  // Dependency edits are DRAFTED like the task fields — mutated locally and
  // only committed (as an add/update/delete diff against the store) on Save,
  // so Cancel discards them. Null until the drawer opens.
  const [depsDraft, setDepsDraft] = useState<ISchedulerDependency[] | null>(null);
  // The stock record raised from a Stock tab row, docked beside the drawer
  // (the site map's area-drawer idiom). Keyed on the OPEN task: a reopen, or
  // a switch to another task, starts without it.
  const stockCompanion = useCompanionState<string>(open ? taskId : null);

  const panelTab = useSchedulerPanelTab();
  const { setPanelTab, taskStockClaimsSet, closeCommitDialog, openPanel } = useSchedulerActions();
  // The commit REVIEW takes the drawer's body over while this task (or this
  // parent) is being promised (2026-09-18 commit UX design, decision 3): the
  // tabs and the footer step aside, the review brings its own.
  const commitTaskId = useSchedulerCommitTaskId();
  const reviewing = mode === 'edit' && !!taskId && commitTaskId === taskId;
  // A parent's review is the list form over its leaves. The walk indexes the
  // whole plan, so it runs once per plan change while reviewing, not per render.
  const reviewSet = useMemo(
    () => (reviewing && taskId ? leafDescendants(data.tasks, taskId).map((leaf) => leaf.id) : []),
    [reviewing, taskId, data.tasks],
  );
  // Stock claims are an EXECUTION fact (ruling 9): the drawer keeps editing
  // them in `draft`, but they are committed through the per-task stock-claims
  // PUT — like status / areas — never through the whole-schedule Save. The
  // live composed task is the base the draft is diffed against. Create mode
  // has no task: the '' sentinel resolves to nothing and `commitStockClaims`
  // returns before it matters.
  const editTaskId = taskId ?? '';
  const liveTask = useSchedulerTask(data.siteId, editTaskId);
  const stale = useSchedulerStale(data.siteId);
  const stockClaimsPersist = useTaskStockClaimsPersist(data.siteId, scheduleNumber, editTaskId);
  // Profession and crew size save themselves on the drawer's Save (2026-09-23
  // amendment), like the areas do in the Overview's map — never via the toolbar.
  const planAutoSave = useTaskPlanAutoSave(data.siteId, scheduleNumber, editTaskId);

  // Keep a ref to the latest data so the reset effect can read it without
  // subscribing to its identity. Immer produces a new object reference on
  // every mutation, so including `data` directly in the reset effect's dep
  // array would clobber the user's in-flight edits whenever any unrelated
  // part of the store changes.
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);
  // The site's day, read (like `data`) through a ref: a midnight rollover must
  // not reset a draft being typed.
  const today = useSiteToday(useSelector(selectSiteTz));
  const todayRef = useRef(today);
  useEffect(() => { todayRef.current = today; }, [today]);
  // The live task as first seen after opening — the base of `rebaseDraft`'s
  // three-way merge (null until observed, so opening itself never rebases).
  const draftBaseRef = useRef<ISchedulerTask | null>(null);

  useEffect(() => {
    if (!open) return;
    // Resetting the draft form when the drawer opens (or the underlying task
    // switches) requires setState inside this effect; the open transition is
    // async so there is no derived-state alternative.
    if (mode === 'edit' && taskId) {
      const existing = dataRef.current.tasks.find((task) => task.id === taskId);
      setDraft(existing ? { ...existing } : null);
    } else {
      const fallbackGroupId = dataRef.current.groups[0]?.id ?? 'g-default';
      setDraft(newTaskTemplate(prefillGroupId ?? prefillTask?.groupId ?? fallbackGroupId, todayRef.current, prefillTask));
    }
    setDepsDraft(dataRef.current.dependencies.map((dependency) => ({ ...dependency })));
    setDeliveryHandoff(null);
    draftBaseRef.current = null;
  }, [open, mode, taskId, prefillGroupId, prefillTask]);

  // The task changed underneath the open drawer (an early finish moved its
  // dates server-side, a rebase, an auto-save): carry the change into every
  // field the user hasn't edited here, so the drawer never shows — or Saves
  // back — the pre-change values.
  useEffect(() => {
    if (!liveTask) return;
    const base = draftBaseRef.current;
    if (base?.id === liveTask.id && !sameJson(base, liveTask)) {
      // The draft is seeded on open, before the live task can first be seen.
      setDraft((prev) => rebaseDraft(base, prev!, liveTask));
    }
    draftBaseRef.current = { ...liveTask };
  }, [liveTask]);

  const group = useMemo(
    () => (draft ? data.groups.find((group) => group.id === draft.groupId) : undefined),
    [draft, data.groups],
  );

  // The Edit tab sees the DRAFT dependency list so its rows/pickers reflect
  // uncommitted edits; everything else in `data` passes through untouched.
  const editTabData = useMemo(
    () => (depsDraft ? { ...data, dependencies: depsDraft } : data),
    [data, depsDraft],
  );

  // Local draft mutators — only offered when the host wired the corresponding
  // commit callback, so surfaces without dependency editing keep the controls
  // disabled exactly as before.
  const handleDraftAddDependency = onAddDependency
    ? (dependency: ISchedulerDependency) =>
        setDepsDraft((prev) => (prev ? [...prev, dependency] : prev))
    : undefined;
  const handleDraftUpdateDependency = onUpdateDependency
    ? (dependency: ISchedulerDependency) =>
        setDepsDraft((prev) => prev?.map((d) => (d.id === dependency.id ? dependency : d)) ?? prev)
    : undefined;
  const handleDraftDeleteDependency = onDeleteDependency
    ? (dependencyId: string) =>
        setDepsDraft((prev) => prev?.filter((d) => d.id !== dependencyId) ?? prev)
    : undefined;

  // Diff the draft against the store and replay it through the host callbacks.
  // Runs on Save only — Cancel/close simply drops the draft.
  const commitDependencyDraft = () => {
    if (!depsDraft) return;
    const before = new Map(data.dependencies.map((dependency) => [dependency.id, dependency]));
    for (const dependency of before.values()) {
      if (!depsDraft.some((d) => d.id === dependency.id)) onDeleteDependency?.(dependency.id);
    }
    for (const dependency of depsDraft) {
      const prev = before.get(dependency.id);
      if (!prev) {
        onAddDependency?.(dependency);
      } else if (
        prev.type !== dependency.type ||
        prev.lagDays !== dependency.lagDays ||
        prev.predecessorId !== dependency.predecessorId ||
        prev.successorId !== dependency.successorId
      ) {
        onUpdateDependency?.(dependency);
      }
    }
  };

  if (!draft) return null;
  // The panel tab is GLOBAL store state, so a tab picked on the Scheduler can
  // arrive at a read-only host that renders neither of the structural tabs.
  // Fall back to Overview rather than leaving `Tabs.Root` on a value no tab
  // matches (an empty body with no footer action).
  // The Edit tab also needs `schedule.edit`; the Overview still shows the task.
  const editTabOffered = allowStructureEdit && canEdit;
  const tabOffered = (panelTab !== 'edit' || editTabOffered) && (panelTab !== 'stock' || allowStructureEdit);
  const activeTab: SchedulerPanelTab = tabOffered ? panelTab : 'overview';
  // Leaving the Stock tab closes its companion — render-time adjust, the
  // same pattern the site map's drawer uses when its record disappears.
  if (stockCompanion.open && activeTab !== 'stock') stockCompanion.close();

  // Commit the draft's stock claims to the execution facet: dispatch + the
  // per-task PUT, only when the set actually changed (a Save that never
  // touched the Stock tab sends nothing) and never on a stale base — the same
  // hard no-op as `useTaskLifecycleActions`, since a stale base must not fire
  // the PUT even though the reducer would freeze the dispatch anyway.
  const commitStockClaims = () => {
    if (!taskId || stale) return;
    const next = draft.stockClaims ?? [];
    if (sameClaims(liveTask?.stockClaims ?? [], next)) return;
    taskStockClaimsSet({ siteId: data.siteId, taskId, stockClaims: next });
    stockClaimsPersist.set(next);
  };

  // The Stock tab's checkout ("Continue to order details") opens the delivery dialog above
  // this drawer — and only that. The claim it was raised for lives only in
  // `draft` until Save; it is persisted (dependencies and claims included,
  // exactly as the footer's Save does) the moment the delivery is actually
  // created, so the order never points at a claim that was never saved. Not
  // on the click: a store dispatch re-renders the whole Gantt, and doing that
  // before the dialog mounts was a visible ~600 ms stall (a cancelled dialog
  // also then leaves nothing behind).
  const handleStockCreateDelivery = (handoff: IOrderHandoff) => setDeliveryHandoff(handoff);
  const persistDraftForDelivery = () => {
    if (liveTask) planAutoSave.saveFields(liveTask, draft);
    onSave(draft);
    commitDependencyDraft();
    commitStockClaims();
  };

  // When creating a sub-task the panel was opened with a prefilled parentId;
  // surface the parent's name in the title so the nesting is explicit.
  const parentTask =
    mode === 'create' && prefillTask?.parentId
      ? data.tasks.find((task) => task.id === prefillTask.parentId)
      : undefined;
  // In edit mode the TASK is the title — the Overview used to repeat the name
  // at the top of its body while the header read the generic "Task details",
  // so the drawer said what kind of thing it was rather than which one.
  const title = parentTask
    ? t('scheduler.panel.createSubTask', { name: parentTask.name })
    : mode === 'create'
      ? t('scheduler.panel.create')
      : draft.name;
  // The plan window, under the title — it moved up from the Overview with the
  // name. Reads the DRAFT, so an Edit-tab date change shows immediately.
  const taskDates = taskWindowLabel(draft);
  // The summaries this task sits under, outermost first. In create mode the
  // draft already carries the prefilled `parentId`, so a new sub-task shows
  // where it is about to land. Only the last few are shown: the cascade is
  // there to place the task, and a header is not an outline.
  const trail = visibleTrail(ancestors(data.tasks, draft));
  const closeButton = (
    <button type="button" className={styles.cancelBtn} onClick={onClose}>{t('scheduler.panel.close')}</button>
  );

  // Stock is booked only for a committed task (the server refuses the rest), by `tasks.execute`.
  const stockWritable = canExecute && draft.committed;
  // Save writes the showing tab: stock claims or the plan (`schedule.edit`).
  const canSave = mode === 'edit' && activeTab === 'stock' ? stockWritable : canEdit;

  return (
    <SchedulerCapabilitiesContext.Provider value={capabilities}>
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal container={portalContainer ?? undefined}>
        <Dialog.Backdrop className={styles.backdrop} />
        <Dialog.Popup className={styles.drawer}>
          {deliveryHandoff ? (
            <StockDeliveryDialog
              siteId={data.siteId}
              handoff={deliveryHandoff}
              onBeforeCreate={persistDraftForDelivery}
              onClose={() => setDeliveryHandoff(null)}
            />
          ) : null}
          {stockCompanion.mounted ? (
            <TaskPanelStockDetail
              siteId={data.siteId}
              materialName={stockCompanion.record}
              open={stockCompanion.open}
              onClose={stockCompanion.close}
              onExited={stockCompanion.unmount}
            />
          ) : null}
          <header className={styles.header}>
            <div className={styles.headerTop}>
              <div className={styles.identity}>
                {(group || trail.length > 0) && (
                  // Where the task sits, as ONE muted breadcrumb over the
                  // title: its group (with the Gantt colour), then the
                  // summaries above it. The name below is the only big text —
                  // a stepped cascade put it last and furthest in, so the eye
                  // met the parents first. Each name is its own <bdi>: a
                  // Hebrew/Japanese name with digits must not reorder its
                  // neighbours.
                  <p className={styles.trail}>
                    {group && (
                      <span className={styles.crumb}>
                        <span
                          className={styles.groupDot}
                          style={{ background: groupColorForGroup(data.tasks, group.id) }}
                          aria-hidden
                        />
                        <bdi className={styles.crumbText}>{group.name}</bdi>
                      </span>
                    )}
                    {trail.map((step, i) => (
                      <Fragment key={step.id ?? step.name}>
                        {(group || i > 0) && <span className={styles.crumbSep} aria-hidden>›</span>}
                        {/* A summary is a way up the tree: it opens in this
                            drawer, as a sub-task row in the Overview does. Not
                            while creating — that would drop the unsaved draft. */}
                        {step.id !== null && mode === 'edit' ? (
                          <button
                            type="button"
                            className={`${styles.crumb} ${styles.crumbLink}`}
                            onClick={() => openPanel({ mode: 'edit', taskId: step.id, initialTab: 'overview' })}
                          >
                            <bdi className={styles.crumbText}>{step.name}</bdi>
                          </button>
                        ) : (
                          <span className={styles.crumb}>
                            <bdi className={styles.crumbText}>{step.name}</bdi>
                          </span>
                        )}
                      </Fragment>
                    ))}
                  </p>
                )}
                {/* <bdi>, not dir="auto": the name's own direction must not flip the
                    heading's alignment away from the drawer's start. */}
                <Dialog.Title className={styles.title}><bdi>{title}</bdi></Dialog.Title>
                {mode === 'edit' && <p className={styles.headerDates}>{taskDates}</p>}
              </div>
              <button type="button" className={styles.iconBtn} onClick={onClose} aria-label={t('scheduler.panel.close')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            {mode === 'create' && (
              <div className={styles.headerMeta}>
                <TaskStatusPill status={draft.status} />
              </div>
            )}
            {/* Where the task stands belongs to the TASK, not to one tab: the
                trail reads the same on Overview, Comments, Edit and Stock. The
                actions that move it stay with the promise, in the Overview. */}
            {mode === 'edit' && taskId && !reviewing && (
              <TaskLadderTrail siteId={data.siteId} taskId={taskId} />
            )}
          </header>

          {reviewing && taskId && (
            reviewSet.length > 0 ? (
              <CommitReviewMany
                siteId={data.siteId}
                taskIds={reviewSet}
                title={t('scheduler.commit.reviewManyTitle', { name: liveTask?.name ?? '' })}
                scheduleNumber={scheduleNumber}
                onClose={closeCommitDialog}
              />
            ) : (
              <CommitReview siteId={data.siteId} taskId={taskId} scheduleNumber={scheduleNumber} onClose={closeCommitDialog} />
            )
          )}

          {reviewing ? null : mode === 'create' ? (
            <div className={styles.createBody}>
              <TaskPanelEditTab
                draft={draft}
                setDraft={setDraft}
                data={editTabData}
                mode={mode}
                openGroupSelectOnMount={openGroupSelect}
                onAddDependency={handleDraftAddDependency}
                onUpdateDependency={handleDraftUpdateDependency}
                onDeleteDependency={handleDraftDeleteDependency}
              />
            </div>
          ) : (
            <Tabs.Root
              value={activeTab}
              onValueChange={(v) => setPanelTab(v as SchedulerPanelTab)}
              className={styles.tabsRoot}
            >
              <Tabs.List className={styles.tabsList}>
                <Tabs.Tab value="overview">{t('scheduler.panel.tabs.overview')}</Tabs.Tab>
                <Tabs.Tab value="comments">{t('scheduler.panel.tabs.comments')}</Tabs.Tab>
                {editTabOffered && <Tabs.Tab value="edit">{t('scheduler.panel.tabs.edit')}</Tabs.Tab>}
                {allowStructureEdit && <Tabs.Tab value="stock">{t('scheduler.panel.tabs.stock')}</Tabs.Tab>}
                <Tabs.Indicator />
              </Tabs.List>
              <Tabs.Panel value="overview">
                {taskId ? <TaskPanelOverviewTab task={draft} data={data} siteId={data.siteId} scheduleNumber={scheduleNumber} topSlot={overviewSlot} /> : null}
              </Tabs.Panel>
              <Tabs.Panel value="comments">
                {taskId ? <TaskPanelCommentsTab taskId={taskId} siteId={data.siteId} scheduleNumber={scheduleNumber} /> : null}
              </Tabs.Panel>
              {editTabOffered && (
                <Tabs.Panel value="edit">
                  <TaskPanelEditTab
                    draft={draft}
                    setDraft={setDraft}
                    data={editTabData}
                    scheduleNumber={scheduleNumber}
                    mode={mode}
                    openGroupSelectOnMount={openGroupSelect}
                    onAddDependency={handleDraftAddDependency}
                    onUpdateDependency={handleDraftUpdateDependency}
                    onDeleteDependency={handleDraftDeleteDependency}
                  />
                </Tabs.Panel>
              )}
              {allowStructureEdit && (
                <Tabs.Panel value="stock">
                  {/* A delivery raised here links back to the task by
                      schedule + id, so the tab needs the loaded schedule. */}
                  {!stockWritable ? (
                    <TaskPanelStockReadOnly
                      claims={draft.stockClaims ?? []}
                      note={canExecute ? t('scheduler.panel.stock.uncommitted') : undefined}
                    />
                  ) : taskId && scheduleNumber ? (
                    <TaskPanelStockTab
                      siteId={data.siteId}
                      scheduleNumber={scheduleNumber}
                      draft={draft}
                      setDraft={setDraft}
                      data={data}
                      onCreateDelivery={handleStockCreateDelivery}
                      // A booking joining an existing order writes straight
                      // away, with no dialog in between — so the same draft
                      // persistence the delivery dialog runs happens here.
                      onBeforeJoin={persistDraftForDelivery}
                      onOpenDelivery={onOpenDelivery}
                      onOpenStock={(level) => stockCompanion.openWith(level.materialName)}
                      onValidity={setStockSaveBlocked}
                      onPicking={setStockPicking}
                    />
                  ) : null}
                </Tabs.Panel>
              )}
            </Tabs.Root>
          )}

          {!stockPicking && !reviewing && <footer className={styles.footer}>
            {/* D-ORCA owns the schedule now, so task deletion persists like any
                other edit — offered to whoever may edit the schedule. */}
            {editTabOffered && mode === 'edit' && (activeTab === 'overview' || activeTab === 'edit') && onDelete && taskId && (
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={() => { onDelete(taskId); onClose(); }}
              >
                {t('scheduler.panel.delete')}
              </button>
            )}
            <div className={styles.spacer} />
            {canSave && (mode === 'create' || (mode === 'edit' && allowStructureEdit && (activeTab === 'edit' || activeTab === 'stock'))) && (
              <>
                <button type="button" className={styles.cancelBtn} onClick={onClose}>{t('scheduler.panel.cancel')}</button>
                <button
                  type="button"
                  className={styles.saveBtn}
                  // Locked while stale, like the toolbar Save: the store drops edits
                  // on a stale base, so a Save here would close the drawer and lose
                  // the draft. Kept open, the draft survives the reload's rebase.
                  disabled={stale || !draft.name.trim() || taskDatesProblem(draft) !== null || (activeTab === 'stock' && stockSaveBlocked)}
                  title={stale ? t('scheduler.stale.banner') : undefined}
                  onClick={() => {
                    // Save is a PLAN write: `updateTask` strips the draft's
                    // execution fields (status/progress/areas/log/claims) and
                    // leaves the execution store untouched, so the immediate-write
                    // surfaces can't be reverted by a stale draft snapshot — no
                    // merge needed. Profession and crew go out first, on their
                    // own PUT, so the plan update finds them already applied.
                    if (liveTask) planAutoSave.saveFields(liveTask, draft);
                    onSave(draft);
                    // Dependency edits commit AFTER the task save: their cascade
                    // (pull-if-pinned on lag changes) must land last, otherwise
                    // updateTask would clobber the cascaded dates with the
                    // draft's stale start/end snapshot.
                    commitDependencyDraft();
                    // The Stock tab's claims go to the execution facet through
                    // their own PUT (ruling 9) — the plan Save above never
                    // carries them.
                    commitStockClaims();
                    // In create mode, SchedulerPage's onSave handler reopens the
                    // panel in edit mode at the Overview tab — don't close here.
                    // In edit mode, close the drawer as before.
                    if (mode !== 'create') onClose();
                  }}
                >
                  {t('scheduler.panel.save')}
                </button>
              </>
            )}
            {/* The Overview's promise rows read as a form, and the task's step
                is their submit: it stands here, where Save stands on the Edit
                tab — after the rows, and in view however long they run. */}
            {mode === 'edit' && activeTab === 'overview' && taskId && (
              <TaskStepActions siteId={data.siteId} taskId={taskId} scheduleNumber={scheduleNumber} fallback={closeButton} />
            )}
            {mode === 'edit' && (activeTab === 'comments' || (activeTab === 'stock' && !canSave)) && closeButton}
          </footer>}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
    </SchedulerCapabilitiesContext.Provider>
  );
}
